/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ScheduleTaskType, TaskRelationDto } from '@gh/builder-shared-data';
import {
  createArrayHelper,
  EntityConfig,
  entityFieldsMerger,
  entityReplaceOrMergeWith,
  fieldStateByEntityConfig,
  onChangeEntityMerger,
  setDirty,
  setEntityField,
  setEntityFields,
} from '@gh/common-shared';
import { WithDirty, WithEntityState } from '@gh/common-shared-data';

import { filter, findIndex, mean, negate, some } from 'lodash';
import { mayHaveResource } from './gantt.model';

export type GenericScheduleTaskInfo = {
  canBeDeleted: boolean;
  hasChildren: boolean;
  mayHaveResource: boolean;
  canChangeCompletionPct: boolean;
};

export type GenericScheduleTaskRecalculationInfo = GenericScheduleTaskInfo & {
  hasIncludedChildren: boolean;
  // list of child task completions, used to implement mean for parent tasks
  completions?: number[];
};

export type GenericScheduleTaskRelation = TaskRelationDto;
export type GenericScheduleTask = WithEntityState<WithDirty<{
  taskNumber: number;
  parentTaskNumber?: number;
  isFromCriticalPath: boolean;
  isExcludeFromGanttChart: boolean;
  type: ScheduleTaskType;
  startDate: Date;
  endDate: Date;
  duration: number;
  mayHaveResource: boolean;
  predecessors?: GenericScheduleTaskRelation[];
  completionPct?: number;
} & GenericScheduleTaskInfo>>;

export const isScheduleTaskStart = (task: GenericScheduleTask) => task.taskNumber === 1;
export const isResourceEditable = (task: GenericScheduleTask) =>  !!task.mayHaveResource && task.taskNumber !== 1;
export const isScheduleTaskCanBeDeleted = (task: GenericScheduleTask) => {
  if (isScheduleTaskStart(task)) {
    return false;
  }
  switch (task.type) {
    case ScheduleTaskType.Summary:
      return true;
    case ScheduleTaskType.Empty:
      return true;
    case ScheduleTaskType.CostCenter:
      return !task.isFromCriticalPath;
    default:
      return false;
  }
};

export const getScheduleTaskId = (task: GenericScheduleTask) => task.taskNumber;

export const setScheduleTaskFields = (task: GenericScheduleTask, values: Hash<any>) => {
  const updatedTask = setEntityFields(task, values);
  return task !== updatedTask ? setDirty(updatedTask) : task;
};

const SCHEDULE_TASK_ENTITY_CONFIG: EntityConfig<GenericScheduleTask> = {
  isExcludeFromGanttChart: { editable: negate(isScheduleTaskStart) },
  isInSupervisorSchedule: { editable: negate(isScheduleTaskStart) },
};

export const scheduleTaskFieldState = fieldStateByEntityConfig(SCHEDULE_TASK_ENTITY_CONFIG);

export const scheduleTaskArray = createArrayHelper(getScheduleTaskId);

// merger for schedule task duration-related properties
export const scheduleTaskMerge = onChangeEntityMerger(
  entityReplaceOrMergeWith(entityFieldsMerger<GenericScheduleTask>([
    'startDate', 'endDate', 'duration',
  ])),
  (task) => setDirty(task!));

export function setScheduleTaskRangeExcludeFromGanttChart(tasks: GenericScheduleTask[],
                                                          { from, to }: { from: number; to: number },
                                                          isExcludeFromGanttChart: boolean): GenericScheduleTask[] {
  return tasks.slice(0, from)
    .concat(tasks.slice(from, to)
      .map((task) => setEntityField(task, 'isExcludeFromGanttChart', isExcludeFromGanttChart)))
    .concat(tasks.slice(to));
}

/**
 * Cleans all references to the given task and its descendants, sets isExcludedFromGantt for all children
 */
export function excludeScheduleTasksFromGanttChart(tasks: GenericScheduleTask[],
                                                   cleanedTaskId: number): GenericScheduleTask[] {
  const range = getScheduleTaskSubtreeRange(tasks, cleanedTaskId);
  return range
    ? setScheduleTaskRangeExcludeFromGanttChart(cleanScheduleTaskRangePredecessors(tasks, range), range, true)
    : tasks;
}

/**
 * Resets isExcludedFromGantt for all children and ancestors
 */
export function includeScheduleTasksIntoGanttChart(tasks: GenericScheduleTask[],
                                                   cleanedTaskId: number): GenericScheduleTask[] {
  const range = getScheduleTaskSubtreeRange(tasks, cleanedTaskId);
  return range
    // reset isExcludeFromGanttChart in ancestors
    ? updateScheduleTaskAncestors(
      // reset isExcludeFromGanttChart in this task and its descendants
      setScheduleTaskRangeExcludeFromGanttChart(tasks, range, false),
      cleanedTaskId,
      (task) => setEntityField(task, 'isExcludeFromGanttChart', false))
    : tasks;
}

export function propagateExcludeTasksFromGanttChart(tasks: GenericScheduleTask[],
                                                    taskId: number,
                                                    isExcludeFromGanttChart: boolean): GenericScheduleTask[] {
  if (isExcludeFromGanttChart) {
    return excludeScheduleTasksFromGanttChart(tasks, taskId);
  } else {
    return includeScheduleTasksIntoGanttChart(tasks, taskId);
  }
}

/**
 * Cleans all references to tasks from the given range.
 */
export function cleanScheduleTaskRangePredecessors(tasks: GenericScheduleTask[],
                                                   range: { from: number; to: number }): GenericScheduleTask[] {
  const idsToClean = tasks.slice(range.from, range.to).map(getScheduleTaskId);
  return idsToClean.reduce(cleanScheduleTaskPredecessors, tasks);
}

/**
 * Cleans all references to the given task.
 *
 * Other tasks can have predecessors pointing out to the given task.
 * We have to clean all these references when we delete task or exclude it from Gantt Chart.
 */
export function cleanScheduleTaskPredecessors(tasks: GenericScheduleTask[],
                                              cleanedTaskId: number): GenericScheduleTask[] {
  return scheduleTaskArray.updateAll(tasks, (task) => {
    if (getScheduleTaskId(task) === cleanedTaskId) {
      return setEntityField(task, 'predecessors', void 0);
    } else if (some(task.predecessors, (predecessor) => predecessor.taskNo === cleanedTaskId)) {
      return setScheduleTaskFields(
        task,
        { 'predecessors': filter(task.predecessors, (predecessor) => predecessor.taskNo !== cleanedTaskId) });
    } else {
      return task;
    }
  });
}

export function updateScheduleTaskAncestors(tasks: GenericScheduleTask[],
                                            firstTaskId: number,
                                            fn: IdentityFn<GenericScheduleTask>): GenericScheduleTask[] {
  let nextTaskId: Maybe<number> = firstTaskId;
  return scheduleTaskArray.updateAllRight(tasks, (task) => {
    const taskId = getScheduleTaskId(task);
    if (!nextTaskId) {
      // we have already found all parents
      return task;
    } else if (nextTaskId === firstTaskId) {
      // we are looking for the first parent
      if (taskId === firstTaskId) {
        // we have found first task
        nextTaskId = task.parentTaskNumber;
      }
      return task;
    } else if (nextTaskId === taskId) {
      // update ancestor
      nextTaskId = task.parentTaskNumber;
      // we have ancestor
      return fn(task);
    } else {
      return task;
    }
  });
}

/**
 * Returns range of tasks which represent given task and all its descendants.
 * This method supposes that array of tasks is ordered by DFS algorithm using pre-ordering (NLR)
 * (https://en.wikipedia.org/wiki/Tree_traversal)
 */
export function getScheduleTaskSubtreeRange(tasks: GenericScheduleTask[],
                                            id: number): Maybe<{ from: number; to: number }> {
  const startIndex = scheduleTaskArray.indexOf(tasks, id);
  if (startIndex < 0) {
    return;
  }

  // flags pointing out if we traverse this task already
  const parentTasks: Hash<boolean> = { [id]: true };
  // find end of subtree
  const endIndex = findIndex(
    tasks,
    ({ taskNumber, parentTaskNumber }) => {
      // Stop searching if one of the following has occurred
      // 1. We found top-level task (parenTaskNumber is nil).
      //    Each top-level task forms own isolated subtree.
      //    So we can stop searching here, because we have found another top-level subtree.
      // 2. We have found task having non-traversed parent (parentTasks[parentTaskNumber] is nil)
      //    Depth first search algorithm with pre-ordering guarantees that all parent tasks reside above children.
      if (!parentTaskNumber || !parentTasks[parentTaskNumber]) {
        return true;
      }
      parentTasks[taskNumber] = true;
      return false;
    },
    startIndex + 1);

  return { from: startIndex, to: endIndex < 0 ? tasks.length : endIndex };
}

export function recalculateScheduleTaskCompletionAncestors(tasks: GenericScheduleTask[],
                                                          changedTaskNumber: number,
                                                          withDirty: boolean = false): GenericScheduleTask[] {
  let result = tasks;
  let parentTaskNumber = scheduleTaskArray.find(result, changedTaskNumber)?.parentTaskNumber;

  while (parentTaskNumber) {
    const parent = scheduleTaskArray.find(result, parentTaskNumber);
    if (!parent) {
      break;
    }

    const directChildren = result.filter((task) => task.parentTaskNumber === parentTaskNumber);
    const includedChildren = directChildren.filter((task) => !task.isExcludeFromGanttChart);
    const hasChildren = directChildren.length > 0;
    const hasIncludedChildren = includedChildren.length > 0;
    const nextCompletionPct = hasIncludedChildren
      ? mean(includedChildren.map((child) => child.completionPct || 0)) || 0
      : (parent.completionPct || 0);
    const setter = withDirty ? setScheduleTaskFields : setEntityFields;

    result = scheduleTaskArray.update(result, parentTaskNumber, (task) => setter(task, {
      completionPct: nextCompletionPct,
      hasChildren,
      canChangeCompletionPct: !hasChildren || !hasIncludedChildren && !task.isExcludeFromGanttChart,
    }));

    parentTaskNumber = parent.parentTaskNumber;
  }

  return result;
}

/**
 * Recalculates and merges view-specific properties into each task.
 */
export function recalculateScheduleTasks(tasks: GenericScheduleTask[],
                                         withDirty: boolean = false): GenericScheduleTask[] {
  // temporary structure to store task-related data
  const tasksInfo: Hash<GenericScheduleTaskRecalculationInfo> = {};

  return scheduleTaskArray.updateAllRight(
    tasks,
    (task) => {
      const { taskNumber, parentTaskNumber, completionPct, canChangeCompletionPct, isExcludeFromGanttChart } = task;
      const taskInfo = ensureTaskInfo(taskNumber);
      // update flag only if it was not set to false
      const canTaskBeDeleted = taskInfo.canBeDeleted === false ? false : isScheduleTaskCanBeDeleted(task);
      taskInfo.canBeDeleted = canTaskBeDeleted;
      const nextCompletionPct = (taskInfo.hasIncludedChildren || taskInfo.hasChildren && isExcludeFromGanttChart
        // if task has children or it is excluded => calculate its completion as mean of child completions
        ? mean(taskInfo.completions)
        // if completion just changed to be user-controled => reset it to 0
        : (!canChangeCompletionPct && taskInfo.canChangeCompletionPct ? 0 : completionPct)) || 0;
      if (parentTaskNumber) {
        const parentTaskInfo = ensureTaskInfo(parentTaskNumber);
        if (!isExcludeFromGanttChart) {
          parentTaskInfo.completions = (parentTaskInfo.completions || []);
          parentTaskInfo.completions.push(nextCompletionPct);
        }
        // update flag only if it was not set to false
        parentTaskInfo.canBeDeleted = parentTaskInfo.canBeDeleted === false ? false : canTaskBeDeleted;
        parentTaskInfo.hasChildren = true;
        parentTaskInfo.hasIncludedChildren = parentTaskInfo.hasIncludedChildren || !isExcludeFromGanttChart;
      }

      return (withDirty ? setScheduleTaskFields : setEntityFields)(task, {
        canBeDeleted: taskInfo.canBeDeleted,
        hasChildren: taskInfo.hasChildren,
        mayHaveResource: mayHaveResource(task, tasks),
        completionPct: nextCompletionPct,
        // type of task becomes Empty if it does not have children anymore
        type: task.type === ScheduleTaskType.Summary && !taskInfo.hasChildren ? ScheduleTaskType.Empty : task.type,
        canChangeCompletionPct: !taskInfo.hasChildren || !taskInfo.hasIncludedChildren && !isExcludeFromGanttChart,
      });
    });

  function ensureTaskInfo(n: number): GenericScheduleTaskRecalculationInfo {
    const task = tasksInfo[n];
    const taskInfo = task || { canBeDeleted: true, hasChildren: false, canChangeCompletionPct: true };
    tasksInfo[n] = taskInfo;
    return taskInfo;
  }
}
